import { useState, useCallback } from 'react';
import { validateForm } from '../utils/validation';

/**
 * Hook for form validation.
 *
 * @param {Object} rules - validation rules keyed by field name.
 *   Each value is an array of validator functions:  (value) => errorString | null
 *
 * @returns {{
 *   errors: Object,        - current field errors { fieldName: message }
 *   validate: Function,    - (data) => boolean — runs all rules, sets errors, returns true when valid
 *   clearError: Function,  - (fieldName) => void — remove a single field error (call on change)
 *   clearErrors: Function, - () => void — remove all errors
 *   isValid: boolean       - true when errors object is empty
 * }}
 */
export default function useFormValidation(rules) {
  const [errors, setErrors] = useState({});

  const validate = useCallback((data) => {
    const result = validateForm(data, rules);
    if (result) {
      setErrors(result);
      return false;
    }
    setErrors({});
    return true;
  }, [rules]);

  const clearError = useCallback((fieldName) => {
    setErrors(prev => {
      if (!prev[fieldName]) return prev;
      const next = { ...prev };
      delete next[fieldName];
      return next;
    });
  }, []);

  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  return {
    errors,
    validate,
    clearError,
    clearErrors,
    isValid: Object.keys(errors).length === 0,
  };
}
